import React, { useEffect, useState } from "react";
import PanelTemplate from "../../templates/Panel";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { toast } from "react-toastify";
import Box from "../../components/Box";
import Icon from "../../components/Icon";
import Input from "../../components/Input";
import PageFooter from "../../components/PageFooter";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import GtmLogo from "./GtmLogo";

const STEPS = [
    {
        title: "Crie um contêiner no Google Tag Manager",
        text: "Entre em tagmanager.google.com com sua conta Google e crie um contêiner do tipo Web para o seu site.",
    },
    {
        title: "Cole o ID aqui",
        text: "O ID fica no topo do painel do Google, no formato GTM-XXXXXXX. Ao salvar, ele passa a carregar em todas as páginas do seu site.",
    },
    {
        title: "Publique suas tags pelo Google",
        text: "A partir daí, cada nova tag é publicada no painel do Google Tag Manager e entra no ar sozinha, sem mexer no site.",
    },
];

const BENEFITS = [
    {
        icon: "zap",
        title: "Sem depender de ninguém",
        text: "Adicione, troque ou remova tags na hora que quiser, sem abrir chamado e sem esperar publicação.",
    },
    {
        icon: "items",
        title: "Tudo num lugar só",
        text: "Google Analytics, Google Ads, Meta Pixel, LinkedIn, TikTok e afins ficam no mesmo painel.",
    },
    {
        icon: "eye",
        title: "Teste antes de publicar",
        text: "O modo de visualização mostra o que dispara em cada página antes de qualquer coisa ir para o ar.",
    },
    {
        icon: "refresh",
        title: "Histórico e volta atrás",
        text: "Cada publicação vira uma versão. Se algo sair errado, dá para voltar para a anterior.",
    },
];

export const Scripts = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gtmId, setGtmId] = useState("");
    const [saved, setSaved] = useState("");
    const [hasCustom, setHasCustom] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        getScripts();
    }, []);

    const getScripts = () => {
        setLoading(true);

        api.get(`/scripts`).then(res => {
            const resource = res.data?.resource ?? {};
            setGtmId(resource.gtm_id ?? "");
            setSaved(resource.gtm_id ?? "");
            setHasCustom(resource.has_custom ?? false);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const updateScripts = () => {
        setSaving(true);
        setErrors([]);

        api.put(`/scripts`, { gtm_id: gtmId }).then(res => {
            // O backend aceita o snippet inteiro colado e devolve só o id, que
            // é o que precisa aparecer de volta no campo.
            const resource = res.data?.resource ?? {};
            setGtmId(resource.gtm_id ?? "");
            setSaved(resource.gtm_id ?? "");
            toast.success(resource.gtm_id ? "Tag atualizada" : "Tag removida");
        }).catch(error => {
            const parserErrors = error?.response?.data?.errors;

            if (error?.response?.status === 422 && Array.isArray(parserErrors)) {
                setErrors(parserErrors);
            } else {
                renderError(error);
            }
        }).then(() => setSaving(false));
    }

    const active = saved !== "";

    return (
        <PanelTemplate>
            <PageHeader left={<h1>Tags e scripts</h1>} />

            <div className="scripts-layout">
                <div>
                    <Box title={`Configuração do GTM`} className={`no-padding`}>
                        <div className="scripts-brand">
                            <GtmLogo size={40} />
                            <div className="scripts-brand-text">
                                <strong>Google Tag Manager</strong>
                                <span>Instale e gerencie suas tags de marketing e análise sem mexer no site.</span>
                            </div>
                            <span className={`scripts-status ${active ? 'is-active' : ''}`}>
                                <Icon name={active ? 'connected' : 'disconnected'} size={13} />
                                {active ? 'Ativo' : 'Não configurado'}
                            </span>
                        </div>

                        <div className="scripts-form">
                            <div className="scripts-id-field">
                                <Input
                                    label={`ID do contêiner`}
                                    value={gtmId}
                                    change={value => setGtmId(value)}
                                    action={updateScripts}
                                />
                            </div>

                            <p className="scripts-field-help">
                                O ID tem o formato <strong>GTM-XXXXXXX</strong>. Pode colar o código inteiro
                                que o Google forneceu — nós extraímos o ID dele.
                            </p>

                            {errors.length > 0 &&
                                <div className="scripts-errors">
                                    <strong>A tag não foi salva. Corrija os pontos abaixo:</strong>
                                    <ul>
                                        {errors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            }

                            {hasCustom &&
                                <div className="scripts-custom">
                                    <Icon name="lock" size={15} />
                                    <span>
                                        Este site também tem códigos personalizados configurados pelo suporte.
                                        Para alterá-los, fale com a gente.
                                    </span>
                                </div>
                            }

                            <a
                                className="scripts-link"
                                href="https://tagmanager.google.com"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Abrir o Google Tag Manager
                                <Icon name="external-link" size={14} />
                            </a>
                        </div>
                    </Box>

                    <Box title={`Como funciona`}>
                        <ol className="scripts-steps">
                            {STEPS.map((step, index) => (
                                <li key={index}>
                                    <span className="scripts-step-number">{index + 1}</span>
                                    <div>
                                        <strong>{step.title}</strong>
                                        <p>{step.text}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <p className="scripts-note">
                            Códigos que precisam rodar de novo a cada troca de página podem não funcionar,
                            porque o site não recarrega ao navegar entre as páginas.
                        </p>
                    </Box>
                </div>

                <Box title={`Por que usar o Tag Manager`}>
                    <ul className="scripts-benefits">
                        {BENEFITS.map((benefit, index) => (
                            <li key={index}>
                                <span className="scripts-benefit-icon">
                                    <Icon name={benefit.icon} size={16} />
                                </span>
                                <div>
                                    <strong>{benefit.title}</strong>
                                    <p>{benefit.text}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Box>
            </div>

            <PageFooter>
                <Button
                    type={`primary`}
                    size={`small`}
                    svg={`save-white`}
                    text={`Salvar alterações`}
                    action={updateScripts}
                    loading={loading || saving}
                />
            </PageFooter>
        </PanelTemplate>
    );
}

export default Scripts;
